import React, { useState, useEffect, useMemo } from 'react';
import {
  Tag, Plus, Trash2, Edit, Save, ArrowLeft, ArrowUp, ArrowDown,
  CheckCircle2, AlertCircle, Eye, EyeOff, Lock, Unlock, Sparkles,
  Layers, Filter, Search, Check, X, Sliders, ChevronDown, ChevronRight,
  HelpCircle, Monitor, Globe, User, HardDrive, FileText, Calendar,
  ListFilter, CheckSquare, Settings2, Play, RefreshCw, Info, Hash, GitBranch,
  Box, Truck, Clock
} from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../../../lib/utils';
import { Dialog, DialogContent, DialogTitle } from '../../../components/Dialog';
import { useSupportSettingsStore, TicketTypeConfig, TicketField, DynamicVisibilityRule } from '../../../store/supportSettingsStore';
import { useTicketStore } from '../../../store/ticketStore';

// Initial System Fields Bank for adding existing fields
export const SYSTEM_FIELDS_BANK: TicketField[] = [
  { id: 'f-asset', key: 'asset_id', label: 'Impacted Asset', type: 'asset', group: 'Asset & Configuration', isMandatory: false, isVisibleAgent: true, isVisiblePortal: true, helpText: 'Link company hardware or workstation asset', isSystem: true },
  { id: 'f-serial', key: 'serial_number', label: 'Hardware Serial Number', type: 'text', group: 'Asset & Configuration', isMandatory: false, isVisibleAgent: true, isVisiblePortal: false, placeholder: 'e.g. SN-98124-US', isSystem: true },
  { id: 'f-location', key: 'office_location', label: 'Office / Site Location', type: 'select', group: 'Requester Info', isMandatory: false, isVisibleAgent: true, isVisiblePortal: true, options: ['HQ - Bangalore', 'San Francisco Office', 'London Hub', 'Remote Work'], isSystem: true },
  { id: 'f-cost-center', key: 'cost_center', label: 'Department Cost Center', type: 'text', group: 'Requester Info', isMandatory: false, isVisibleAgent: true, isVisiblePortal: false, placeholder: 'e.g. CC-ENG-402', isSystem: true },
  { id: 'f-due-date', key: 'target_due_date', label: 'Target Completion Date', type: 'date', group: 'Details', isMandatory: false, isVisibleAgent: true, isVisiblePortal: false, isSystem: true },
  { id: 'f-impacted-service', key: 'impacted_service', label: 'Impacted Business Service', type: 'select', group: 'Details', isMandatory: false, isVisibleAgent: true, isVisiblePortal: true, options: ['Corporate VPN & Zero Trust', 'Google Workspace Email', 'Production PostgreSQL DB', 'Customer Billing Portal', 'HRMS & Payroll'], isSystem: true },
  { id: 'f-access-role', key: 'access_role_level', label: 'Requested IAM / Access Role', type: 'select', group: 'Details', isMandatory: false, isVisibleAgent: true, isVisiblePortal: true, options: ['Read Only', 'Standard User', 'Operator / Admin', 'Superuser / Root'], isSystem: true },
  { id: 'f-cab-approval', key: 'cab_approval_required', label: 'CAB Change Approval Required', type: 'checkbox', group: 'Details', isMandatory: false, isVisibleAgent: true, isVisiblePortal: false, isSystem: true },
  { id: 'f-rollback', key: 'rollback_plan', label: 'Rollback & Contingency Plan', type: 'textarea', group: 'Details', isMandatory: false, isVisibleAgent: true, isVisiblePortal: false, placeholder: 'Steps to safely revert if change fails...', isSystem: true },
  { id: 'f-workaround', key: 'workaround_notes', label: 'Temporary Workaround Instructions', type: 'textarea', group: 'Resolution & Closure', isMandatory: false, isVisibleAgent: true, isVisiblePortal: true, placeholder: 'Interim workaround for impacted users...', isSystem: true },
  { id: 'f-root-cause', key: 'root_cause_analysis', label: 'Root Cause Classification', type: 'select', group: 'Resolution & Closure', isMandatory: false, isVisibleAgent: true, isVisiblePortal: false, options: ['Software Defect', 'Hardware Failure', 'Configuration Mistake', 'Network Congestion', 'Third-Party Outage'], isSystem: true },
];

export function TicketTypesView() {
  const {
    ticketTypes: storeTicketTypes,
    fetchTicketTypes,
    saveTicketType,
    deleteTicketType,
    isLoadingTicketTypes,
    extendedWorkflows: storeExtendedWorkflows,
    fetchExtendedWorkflows,
  } = useSupportSettingsStore();

  const [ticketTypes, setTicketTypes] = useState<TicketTypeConfig[]>(storeTicketTypes || []);
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    void fetchTicketTypes();
    void fetchExtendedWorkflows();
  }, [fetchTicketTypes, fetchExtendedWorkflows]);

  useEffect(() => {
    if (storeTicketTypes && storeTicketTypes.length > 0) {
      setTicketTypes(storeTicketTypes);
    }
  }, [storeTicketTypes]);

  // All available system workflows for multi-workflow mapping
  const availableWorkflowsList = useMemo(() => {
    const list: Array<{ id: string; name: string; ticketType?: string; description?: string }> = [];
    if (storeExtendedWorkflows && storeExtendedWorkflows.length > 0) {
      storeExtendedWorkflows.forEach((ew) => {
        list.push({
          id: ew.id,
          name: ew.name,
          ticketType: ew.ticketType,
          description: ew.description,
        });
      });
    }
    const STANDARD_WFS = [
      { id: 'wf-inc', name: 'Incident', ticketType: 'Incident', description: 'Standard ITIL incident lifecycle' },
      { id: 'wf-prb', name: 'Problem', ticketType: 'Problem', description: 'Root cause analysis and resolution' },
      { id: 'wf-sr', name: 'Service Request', ticketType: 'Service Request', description: 'User request fulfillment & procurement' },
      { id: 'wf-chg', name: 'Change', ticketType: 'Change', description: 'Change planning and deployment lifecycle' },
      { id: 'wf-app', name: 'Approval', ticketType: 'Approval', description: 'Multi-stage approval workflow' },
      { id: 'wf-hr', name: 'HR', ticketType: 'HR Request', description: 'Confidential human resources requests' },
      { id: 'wf-dev', name: 'Development', ticketType: 'Development', description: 'Bug, feature, and enhancement backlog lifecycle' },
      { id: 'wf-rel', name: 'Release', ticketType: 'Release', description: 'Preview, testing, and production release pipeline' },
    ];
    STANDARD_WFS.forEach((sw) => {
      if (!list.some((l) => l.name.toLowerCase() === sw.name.toLowerCase())) {
        list.push(sw);
      }
    });
    return list;
  }, [storeExtendedWorkflows]);
  
  // Detail View State: Only 'general' and 'field_list'
  const [activeTab, setActiveTab] = useState<'general' | 'field_list'>('general');
  const isEditMode = selectedTypeId !== null;
  const [originalType, setOriginalType] = useState<TicketTypeConfig | null>(null);
  
  // Field Editor Drawer/Modal State
  const [editingField, setEditingField] = useState<TicketField | null>(null);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [addFieldTab, setAddFieldTab] = useState<'existing' | 'custom'>('existing');

  // New Custom Field Form State
  const [customFieldForm, setCustomFieldForm] = useState<Partial<TicketField>>({
    label: '',
    key: '',
    type: 'text',
    group: 'Details',
    isMandatory: false,
    isVisibleAgent: true,
    isVisiblePortal: true,
    placeholder: '',
    helpText: '',
    options: ['Option 1', 'Option 2', 'Option 3'],
  });

  // Ticket Numbering State
  const { ticketNumberConfig, updateTypeNumberConfig, updateTicketNumberConfig } = useTicketStore();
  const [selectedNumType, setSelectedNumType] = useState<string>('incident');

  // The 12 standard/configured ticket types (team independent)
  const DEFAULT_NUMBERING_TYPES = [
    { key: 'incident', name: 'Incident', code: 'INC', defaultPrefix: 'INC#', defaultWorkflow: 'Incident', linkedWorkflowName: 'Incident', desc: 'Unplanned interruption or outage of IT service or asset' },
    { key: 'problem', name: 'Problem', code: 'PRB', defaultPrefix: 'PRB#', defaultWorkflow: 'Problem', linkedWorkflowName: 'Problem', desc: 'Investigation of underlying root causes behind recurring incidents' },
    { key: 'service-request', name: 'Service Request', code: 'SR', defaultPrefix: 'SR#', defaultWorkflow: 'Service Request', linkedWorkflowName: 'Service Request', desc: 'Formal user request for equipment, software, or service fulfillment' },
    { key: 'change', name: 'Change', code: 'CHG', defaultPrefix: 'CHG#', defaultWorkflow: 'Change', linkedWorkflowName: 'Change', desc: 'Planned addition, modification, or removal of authorized services' },
    { key: 'approval', name: 'Approval', code: 'APP', defaultPrefix: 'APP#', defaultWorkflow: 'Approval', linkedWorkflowName: 'Approval', desc: 'Management or financial approval requests' },
    { key: 'hr-request', name: 'HR Request', code: 'HR', defaultPrefix: 'HR#', defaultWorkflow: 'HR', linkedWorkflowName: 'HR', desc: 'Confidential human resources requests and inquiries' },
    { key: 'bug', name: 'Bug', code: 'BUG', defaultPrefix: 'BUG#', defaultWorkflow: 'Development', linkedWorkflowName: 'Development', desc: 'Software bugs and defect tracking' },
    { key: 'feature', name: 'Feature', code: 'FEAT', defaultPrefix: 'FEAT#', defaultWorkflow: 'Development', linkedWorkflowName: 'Development', desc: 'Software feature requests' },
    { key: 'enhancement', name: 'Enhancement', code: 'ENH', defaultPrefix: 'ENH#', defaultWorkflow: 'Development', linkedWorkflowName: 'Development', desc: 'Product and performance enhancements' },
    { key: 'preview', name: 'Preview', code: 'PR', defaultPrefix: 'PR#', defaultWorkflow: 'Release', linkedWorkflowName: 'Release', desc: 'Pre-release staging and preview deployments' },
    { key: 'testing', name: 'Testing', code: 'TEST', defaultPrefix: 'TEST#', defaultWorkflow: 'Release', linkedWorkflowName: 'Release', desc: 'QA testing and release validation' },
    { key: 'production', name: 'Production', code: 'PRO', defaultPrefix: 'PRO#', defaultWorkflow: 'Release', linkedWorkflowName: 'Release', desc: 'Production release deployment tracking' },
  ];

  const allNumberingTypes = useMemo(() => {
    if (ticketTypes && ticketTypes.length > 0) {
      return ticketTypes.map((tt: any) => {
        const key = (tt.code || tt.tag || tt.id || '').toLowerCase();
        const pfx = tt.tagPrefix 
          ? `${tt.tagPrefix}#` 
          : (tt.prefix || `${tt.code || tt.tag || 'TCK'}#`);
        const wf = tt.linkedWorkflowName || tt.linked_workflow_name || tt.defaultWorkflow || tt.workflowName || tt.activeWorkflowName || '';
        return {
          key,
          id: tt.id,
          name: tt.name,
          code: tt.code || tt.tag || tt.name.substring(0, 3).toUpperCase(),
          teamName: tt.teamName || (tt.team_id ? '' : 'None (Team Independent)'),
          defaultPrefix: pfx,
          defaultWorkflow: wf,
          linkedWorkflowName: wf,
          desc: tt.description || `${tt.name} lifecycle ticket type`,
          digits: tt.paddingDigits || 6,
          sla: tt.sla || 'Standard SLA Policy',
          openTickets: tt.openTickets ?? tt.open_tickets ?? 0,
          status: tt.status || 'Active',
          activeWorkflowId: tt.activeWorkflowId || tt.workflow_id,
        };
      });
    }
    return DEFAULT_NUMBERING_TYPES;
  }, [ticketTypes]);

  const globalSequence = ticketNumberConfig?.sequence ?? 65;
  const currentTypeConfig = ticketNumberConfig?.byType?.[selectedNumType] || 
    (selectedNumType === 'incident' ? { prefix: ticketNumberConfig?.prefix || 'INC#', digits: ticketNumberConfig?.digits || 5, sequence: globalSequence } : null) ||
    { prefix: `${allNumberingTypes.find(t => t.key === selectedNumType)?.defaultPrefix || 'TCK#'}`, digits: 5, sequence: globalSequence };

  const [numPrefix, setNumPrefix] = useState(currentTypeConfig.prefix);
  const [numDigits, setNumDigits] = useState(currentTypeConfig.digits);
  const [numSequence, setNumSequence] = useState(globalSequence);

  useEffect(() => {
    const cfg = ticketNumberConfig?.byType?.[selectedNumType] || 
      (selectedNumType === 'incident' ? { prefix: ticketNumberConfig?.prefix || 'INC#', digits: ticketNumberConfig?.digits || 5, sequence: globalSequence } : null) ||
      { prefix: `${allNumberingTypes.find(t => t.key === selectedNumType)?.defaultPrefix || 'TCK#'}`, digits: 5, sequence: globalSequence };
    setNumPrefix(cfg.prefix);
    setNumDigits(cfg.digits);
    setNumSequence(ticketNumberConfig?.sequence ?? 65);
  }, [selectedNumType, ticketNumberConfig, globalSequence, allNumberingTypes]);

  const previewTicketId = `${numPrefix}${String(numSequence).padStart(Number(numDigits), '0')}`;

  const selectedType = ticketTypes.find((t) => t.id === selectedTypeId) || null;

  // Open a ticket type directly in the editor panel.
  const handleCustomize = (tt: (typeof allNumberingTypes)[0]) => {
    let found = ticketTypes.find(t => 
      t.id === (tt as any).id ||
      t.id === tt.key || 
      t.id === `tt-${tt.key}` ||
      t.code?.toUpperCase() === tt.code?.toUpperCase() || 
      t.name?.toLowerCase() === tt.name?.toLowerCase()
    );

    const typeCfg = ticketNumberConfig?.byType?.[tt.key] || 
      (tt.key === 'incident' ? { prefix: ticketNumberConfig?.prefix || 'INC#', digits: ticketNumberConfig?.digits || 5, sequence: globalSequence } : null) ||
      { prefix: tt.defaultPrefix, digits: (tt as any).digits || 5, sequence: globalSequence };

    if (!found) {
      const newType: TicketTypeConfig = {
        id: `tt-${tt.key}`,
        name: tt.name,
        code: tt.code,
        prefix: typeCfg.prefix,
        paddingDigits: typeCfg.digits || 5,
        nextSequence: globalSequence,
        icon: 'Tag',
        sla: 'Standard Support Tier',
        defaultQueue: 'Support Team',
        defaultPriority: 'Medium',
        description: tt.desc,
        status: 'Active',
        fields: [
          { id: `f-${tt.key}-1`, key: 'summary', label: `${tt.name} Summary`, type: 'text', group: 'Details', isMandatory: true, isVisibleAgent: true, isVisiblePortal: true, placeholder: `Enter ${tt.name.toLowerCase()} summary...`, isSystem: true },
          { id: `f-${tt.key}-2`, key: 'description', label: 'Detailed Description', type: 'textarea', group: 'Details', isMandatory: true, isVisibleAgent: true, isVisiblePortal: true, placeholder: 'Provide full details, error logs, and business impact...', isSystem: true },
          { id: `f-${tt.key}-3`, key: 'impacted_service', label: 'Impacted Business Service', type: 'select', group: 'Details', isMandatory: false, isVisibleAgent: true, isVisiblePortal: true, options: ['Corporate VPN & Zero Trust', 'Google Workspace Email', 'Production PostgreSQL DB', 'Customer Billing Portal', 'HRMS & Payroll'], isSystem: true },
          { id: `f-${tt.key}-4`, key: 'office_location', label: 'Site / Location', type: 'select', group: 'Requester Info', isMandatory: false, isVisibleAgent: true, isVisiblePortal: true, options: ['HQ - Bangalore', 'San Francisco Office', 'London Hub', 'Remote Work'], isSystem: true },
        ],
      };
      setTicketTypes(prev => [...prev, newType]);
      found = newType;
    } else {
      if (!found.prefix || !found.paddingDigits) {
        const updatedFound: TicketTypeConfig = {
          ...found,
          prefix: found.prefix || typeCfg.prefix,
          paddingDigits: found.paddingDigits || typeCfg.digits || 5,
          nextSequence: found.nextSequence || globalSequence,
        };
        found = updatedFound;
        setTicketTypes(prev => prev.map(t => t.id === updatedFound.id ? updatedFound : t));
      }
    }
    if (!found.workflows || found.workflows.length === 0) {
      const defaultWfName = found.defaultWorkflow || `${found.name} Workflow`;
      found.workflows = [defaultWfName];
      found.defaultWorkflow = defaultWfName;
    }

    const isHr = ['hr', 'leave', 'onboarding', 'offboarding', 'payroll'].some(k =>
      String(found?.name || tt.name || '').toLowerCase().includes(k) ||
      String(found?.code || tt.code || '').toLowerCase().startsWith(k) ||
      String((found as any)?.teamName || (tt as any)?.teamName || '').toLowerCase().includes(k)
    );
    if (found.enableAsset === undefined) found.enableAsset = !isHr;
    if (found.enableVendor === undefined) found.enableVendor = !isHr;
    if (found.enableSla === undefined) found.enableSla = true;

    setOriginalType(JSON.parse(JSON.stringify(found)));
    setSelectedTypeId(found.id);
    setSelectedNumType(tt.key);
    setNumPrefix(found.prefix || typeCfg.prefix);
    setNumDigits(found.paddingDigits || typeCfg.digits || 5);
    setNumSequence(globalSequence);
    // Customizing a type is always an edit workflow, presented in the side panel.
    setActiveTab('general');
  };

  const handleBackToList = () => {
    if (originalType) {
      setTicketTypes((current) => current.map((type) => type.id === originalType.id ? originalType : type));
    }
    setSelectedTypeId(null);
    setOriginalType(null);
    setEditingField(null);
    setShowAddFieldModal(false);
  };

  // Apply & Save: applies both numbering rules and ticket type configurations
  const handleApplyAndSave = async () => {
    if (!selectedType || !isEditMode) return;
    try {
      updateTypeNumberConfig(selectedNumType, {
        prefix: numPrefix,
        digits: Number(numDigits),
        sequence: Number(numSequence),
      });

      if (Number(numSequence) !== globalSequence) {
        updateTicketNumberConfig({ sequence: Number(numSequence) });
      }

      const updatedType: TicketTypeConfig = {
        ...selectedType,
        prefix: numPrefix,
        paddingDigits: Number(numDigits),
        nextSequence: Number(numSequence),
      };

      setTicketTypes(prev => prev.map(t => t.id === selectedType.id ? updatedType : t));
      await saveTicketType(updatedType);
      setSelectedTypeId(null);
      setOriginalType(null);
      toast.success(`Ticket type "${updatedType.name}" applied and saved!`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save ticket type configuration');
    }
  };

  // Field List Reordering
  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    if (!selectedType || !isEditMode) return;
    const fields = [...selectedType.fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= fields.length) return;

    const temp = fields[index];
    fields[index] = fields[targetIndex];
    fields[targetIndex] = temp;

    setTicketTypes((prev) =>
      prev.map((t) => (t.id === selectedType.id ? { ...t, fields } : t))
    );
  };

  // Toggle Mandatory
  const handleToggleMandatory = (fieldId: string) => {
    if (!selectedType || !isEditMode) return;
    const fields = selectedType.fields.map((f) =>
      f.id === fieldId ? { ...f, isMandatory: !f.isMandatory } : f
    );
    setTicketTypes((prev) =>
      prev.map((t) => (t.id === selectedType.id ? { ...t, fields } : t))
    );
    const updated = fields.find((f) => f.id === fieldId);
    toast.success(`Field "${updated?.label}" mandatory set to ${updated?.isMandatory ? 'Required' : 'Optional'}`);
  };

  // Delete Field
  const handleDeleteField = (fieldId: string, label: string) => {
    if (!selectedType || !isEditMode) return;
    const fields = selectedType.fields.filter((f) => f.id !== fieldId);
    setTicketTypes((prev) =>
      prev.map((t) => (t.id === selectedType.id ? { ...t, fields } : t))
    );
    toast.success(`Field "${label}" removed from ticket form`);
  };

  // Save Field Property Edits (from Modal)
  const handleSaveFieldProperties = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedType || !editingField) return;

    const fields = selectedType.fields.map((f) =>
      f.id === editingField.id ? editingField : f
    );
    setTicketTypes((prev) =>
      prev.map((t) => (t.id === selectedType.id ? { ...t, fields } : t))
    );
    setEditingField(null);
    toast.success(`Field "${editingField.label}" properties updated!`);
  };

  // Add Existing Field from Bank
  const handleAddExistingField = (bankField: TicketField) => {
    if (!selectedType || !isEditMode) return;
    if (selectedType.fields.some((f) => f.key === bankField.key)) {
      toast.error(`Field "${bankField.label}" already exists on this ticket type`);
      return;
    }
    const newField: TicketField = {
      ...bankField,
      id: `f-${Date.now()}`,
    };
    const fields = [...selectedType.fields, newField];
    setTicketTypes((prev) =>
      prev.map((t) => (t.id === selectedType.id ? { ...t, fields } : t))
    );
    setShowAddFieldModal(false);
    toast.success(`Added "${bankField.label}" to ${selectedType.name}`);
  };

  // Create & Add New Custom Field
  const handleCreateCustomField = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedType || !isEditMode || !customFieldForm.label) {
      toast.error('Field Label is required');
      return;
    }
    const generatedKey = (customFieldForm.key || customFieldForm.label.toLowerCase().replace(/[^a-z0-9]/g, '_'));
    const newField: TicketField = {
      id: `f-cust-${Date.now()}`,
      key: generatedKey,
      label: customFieldForm.label,
      type: customFieldForm.type || 'text',
      group: customFieldForm.group || 'Details',
      isMandatory: customFieldForm.isMandatory || false,
      isVisibleAgent: customFieldForm.isVisibleAgent ?? true,
      isVisiblePortal: customFieldForm.isVisiblePortal ?? true,
      placeholder: customFieldForm.placeholder || '',
      helpText: customFieldForm.helpText || '',
      options: customFieldForm.options || [],
      isSystem: false,
    };
    const fields = [...selectedType.fields, newField];
    setTicketTypes((prev) =>
      prev.map((t) => (t.id === selectedType.id ? { ...t, fields } : t))
    );
    setShowAddFieldModal(false);
    setCustomFieldForm({
      label: '',
      key: '',
      type: 'text',
      group: 'Details',
      isMandatory: false,
      isVisibleAgent: true,
      isVisiblePortal: true,
      placeholder: '',
      helpText: '',
      options: ['Option 1', 'Option 2'],
    });
    toast.success(`Custom field "${newField.label}" created!`);
  };

  // Group fields for Field List view
  const fieldGroups: Array<TicketField['group']> = [
    'Details',
    'Asset & Configuration',
    'Requester Info',
    'Resolution & Closure',
    'Custom'
  ];

  return (
    <div className="space-y-6 text-foreground text-xs font-sans animate-in fade-in-50 duration-150">
      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* 1. MASTER VIEW: TICKET TYPES & CONTINUOUS NUMBERING TABLE */}
      {/* ═════════════════════════════════════════════════════════════════ */}
        <div className="rounded-xl border border-border bg-card p-4 shadow-xs space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-3 border-b border-border">
            <div className="flex items-center gap-2.5 flex-wrap w-full justify-between">
              <div className="relative">
                <Search className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search types, prefixes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 pl-8 pr-3 rounded-lg border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring w-64"
                />
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary border border-border text-foreground text-xs font-medium" title="Sequences are maintained atomically per prefix tag in support.ticket_number_sequences">
                <span className="text-muted-foreground">Counter Scope:</span>
                <span className="font-mono font-semibold text-primary">Per-Type Isolated</span>
              </div>
            </div>
          </div>

          <div className="w-full rounded-lg border border-border bg-card overflow-hidden">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-secondary/40 text-muted-foreground text-xs font-semibold border-b border-border">
                <tr className="h-9">
                  <th className="px-4 py-2 font-medium">Ticket Type</th>
                  <th className="px-4 py-2 font-medium w-24">Prefix</th>
                  <th className="px-4 py-2 font-medium">Linked Workflow</th>
                  <th className="px-4 py-2 font-medium">SLA Policy</th>
                  <th className="px-4 py-2 font-medium w-28">Open Tickets</th>
                  <th className="px-4 py-2 font-medium w-24">Status</th>
                  <th className="px-4 py-2 font-medium text-right w-20">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {allNumberingTypes.filter((t) => {
                  if (!searchQuery) return true;
                  const q = searchQuery.toLowerCase();
                  return t.name.toLowerCase().includes(q) || t.code.toLowerCase().includes(q) || t.defaultPrefix.toLowerCase().includes(q);
                }).map((tt) => {
                  const wfMatch = availableWorkflowsList.find((w) => w.ticketType?.toLowerCase() === tt.name.toLowerCase() || w.name.toLowerCase().includes(tt.name.toLowerCase()));
                  const wfName = (tt as any).defaultWorkflow || wfMatch?.name || 'Default Lifecycle Workflow';
                  const typeSeq = (tt as any).currentSequence ?? globalSequence;
                  const typeCfg = ticketNumberConfig?.byType?.[tt.key] || 
                    (tt.key === 'incident' ? { prefix: ticketNumberConfig?.prefix || 'INC#', digits: ticketNumberConfig?.digits || 5, sequence: typeSeq } : null) ||
                    { prefix: tt.defaultPrefix, digits: (tt as any).digits || 5, sequence: typeSeq };
                  const isSelected = selectedNumType === tt.key;
                  const openCount = (tt as any).openTickets ?? 0;
                  const statusLabel = (tt as any).status || 'Active';

                  return (
                    <tr 
                      key={tt.key} 
                      className={cn(
                        'h-10 hover:bg-muted/40 transition-colors',
                        isSelected && 'bg-primary/5 font-medium'
                      )}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-foreground text-xs">{tt.name}</span>
                          {(tt as any).teamName && (
                            <span className="text-[10px] font-medium text-muted-foreground px-1.5 py-0.2 rounded bg-muted/80 shrink-0">
                              {(tt as any).teamName}
                            </span>
                          )}
                          {isSelected && (
                            <span className="text-[10px] bg-primary/10 text-primary border border-primary/20 px-1 py-0.2 rounded font-semibold shrink-0">
                              Selected
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs font-semibold text-foreground">
                        <span className="px-1.5 py-0.5 rounded bg-muted/60 border border-border/60">
                          {typeCfg.prefix}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1.5 text-xs text-foreground hover:text-primary transition-colors cursor-pointer truncate max-w-xs" title={wfName}>
                          <GitBranch className="size-3 text-muted-foreground shrink-0" />
                          <span className="truncate">{wfName}</span>
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs">
                        {(tt as any).sla || 'Standard SLA Policy'}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground tabular-nums">
                        {openCount} active
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1.5 text-xs text-foreground">
                          <span className={cn(
                            "size-1.5 rounded-full shrink-0",
                            statusLabel === 'Active' ? "bg-success" : "bg-warning"
                          )} />
                          <span>{statusLabel}</span>
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            handleCustomize(tt);
                          }}
                          className="inline-flex h-7 items-center gap-1 rounded-md px-2.5 text-xs font-medium text-foreground bg-card border border-border hover:bg-secondary transition-colors"
                          title="Edit ticket type and fields"
                        >
                          <Edit className="size-3" /> Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* 2. DETAIL VIEW: SELECTED TICKET TYPE (CUSTOMIZE VIEW) */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {selectedType && (
        <Dialog open onOpenChange={(open) => { if (!open) handleBackToList(); }}>
        <DialogContent side="right" className="max-w-2xl gap-4 p-5" aria-describedby={undefined}>
          <div className="space-y-3 border-b border-border pb-3 pr-8">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <DialogTitle>Edit {selectedType.name}</DialogTitle>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="px-2 py-0.5 rounded text-[11px] font-mono bg-muted text-foreground border border-border">
                  Code: <strong className="font-semibold">{selectedType.code}</strong>
                </span>
                <span className="px-2 py-0.5 rounded text-[11px] font-mono bg-muted text-foreground border border-border">
                  Padding: <strong className="font-semibold">{numDigits} Digits</strong>
                </span>
                <span className="px-2 py-0.5 rounded text-[11px] font-mono bg-muted text-foreground border border-border">
                  Counter: <strong className="font-semibold">#{numSequence}</strong>
                </span>
                <span className="px-2 py-0.5 rounded text-[11px] font-mono bg-primary/10 text-primary border border-primary/20">
                  Next ID: <strong className="font-semibold">{previewTicketId}</strong>
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {([{ id: 'general', label: 'General and defaults' }, { id: 'field_list', label: 'Form fields' }] as const).map((tab) => (
                <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={cn('h-9 border-b-2 px-3 text-sm font-medium', activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground')}>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'general' && (
            <div className="space-y-5">
              {/* Properties Card */}
              <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
                <div className="border-b border-border pb-3">
                  <h3 className="font-display text-card-title text-foreground">General Properties &amp; Default SLA</h3>
                  <p className="text-xs text-muted-foreground">Define base routing queue, priority escalation, and classification metadata for {selectedType.name}.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Ticket Type Name *</label>
                    <input
                      type="text"
                      value={selectedType.name}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const name = e.target.value;
                        setTicketTypes(ticketTypes.map((t) => (t.id === selectedType.id ? { ...t, name } : t)));
                      }}
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="font-semibold text-foreground block mb-1">Type Code (e.g. INC, REQ, CHAN, BUG) *</label>
                    <input
                      type="text"
                      value={selectedType.code}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const code = e.target.value.toUpperCase();
                        setTicketTypes(ticketTypes.map((t) => (t.id === selectedType.id ? { ...t, code } : t)));
                      }}
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-semibold text-foreground block mb-1">Description</label>
                  <textarea
                    rows={2}
                    value={selectedType.description}
                    disabled={!isEditMode}
                    onChange={(e) => {
                      const description = e.target.value;
                      setTicketTypes(ticketTypes.map((t) => (t.id === selectedType.id ? { ...t, description } : t)));
                    }}
                    className="w-full rounded-md border border-input bg-background p-2 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Default SLA Target</label>
                    <input
                      type="text"
                      value={selectedType.sla}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const sla = e.target.value;
                        setTicketTypes(ticketTypes.map((t) => (t.id === selectedType.id ? { ...t, sla } : t)));
                      }}
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="font-semibold text-foreground block mb-1">Default Routing Queue</label>
                    <select
                      value={selectedType.defaultQueue}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const defaultQueue = e.target.value;
                        setTicketTypes(ticketTypes.map((t) => (t.id === selectedType.id ? { ...t, defaultQueue } : t)));
                      }}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <option value="Support Team">Support Team (L1/L2)</option>
                      <option value="Infrastructure &amp; DevOps">Infrastructure &amp; DevOps</option>
                      <option value="Hardware &amp; Onsite">Hardware &amp; Onsite</option>
                      <option value="Network Operations">Network Operations</option>
                    </select>
                  </div>

                  <div>
                    <label className="font-semibold text-foreground block mb-1">Default Priority</label>
                    <select
                      value={selectedType.defaultPriority}
                      disabled={!isEditMode}
                      onChange={(e) => {
                        const defaultPriority = e.target.value;
                        setTicketTypes(ticketTypes.map((t) => (t.id === selectedType.id ? { ...t, defaultPriority } : t)));
                      }}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Urgent">Urgent</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Numbering & Prefix Settings Card */}
              <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
                <div className="border-b border-border pb-3 flex items-center justify-between">
                  <div>
                    <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                      <Hash className="size-4 text-primary" /> Ticket ID Prefix &amp; Continuous Counter
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Configure dedicated ticket prefix and zero-padding length. Continuous counter increments smoothly across all types.
                    </p>
                  </div>
                  <div className="px-3 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary font-mono text-xs font-semibold">
                    Next ID: {previewTicketId}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Ticket Prefix <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="text"
                      value={numPrefix}
                      disabled={!isEditMode}
                      onChange={(e) => setNumPrefix(e.target.value)}
                      placeholder="e.g. INC#, SER#, CNG#, BUG#"
                      className="w-full px-3 py-2 bg-background border border-input rounded-md text-xs font-mono font-semibold text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                      required
                    />

                    {/* Quick Suggestions */}
                    {isEditMode && (
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">Suggestions:</span>
                        {[
                          `${selectedType.code}#`,
                          `${selectedType.code}-`,
                          'INC#',
                          'SER#',
                          'CNG#',
                          'BUG#',
                          'ACC#',
                          'FEAT#'
                        ].filter((v, i, a) => a.indexOf(v) === i).map((sug) => (
                          <button
                            key={sug}
                            type="button"
                            onClick={() => setNumPrefix(sug)}
                            className={cn(
                              'px-1.5 py-0.5 rounded text-xs font-mono border transition-colors cursor-pointer',
                              numPrefix === sug
                                ? 'bg-primary text-primary-foreground border-primary font-semibold'
                                : 'bg-background hover:bg-muted text-muted-foreground border-border'
                            )}
                          >
                            {sug}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Number Padding Width <span className="text-destructive">*</span>
                    </label>
                    <select
                      value={numDigits}
                      disabled={!isEditMode}
                      onChange={(e) => setNumDigits(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-background border border-input rounded-md text-xs font-semibold text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <option value={4}>4 Digits (e.g. 0001)</option>
                      <option value={5}>5 Digits (e.g. 00001) - Standard</option>
                      <option value={6}>6 Digits (e.g. 000001)</option>
                      <option value={7}>7 Digits (e.g. 0000001)</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">Zero-padded sequential length.</p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">
                      Continuous Counter Sequence <span className="text-destructive">*</span>
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={numSequence}
                      disabled={!isEditMode}
                      onChange={(e) => setNumSequence(Math.max(1, Number(e.target.value)))}
                      className="w-full px-3 py-2 bg-background border border-input rounded-md text-xs font-mono text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">Shared continuous counter for all ticket types.</p>
                  </div>
                </div>

                {/* Live Sequence Preview Card */}
                <div className="p-4 rounded-xl bg-card border border-border space-y-2.5 shadow-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground block">
                      Upcoming Sequence Sample for <span className="font-semibold text-foreground">{selectedType.name}</span>:
                    </span>
                    <span className="text-xs text-muted-foreground font-mono">
                      Format: &lt;Prefix&gt;&lt;Padded Sequence&gt;
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[0, 1, 2, 3, 4].map((offset) => {
                      const idSample = `${numPrefix}${String(Number(numSequence) + offset).padStart(Number(numDigits), '0')}`;
                      return (
                        <span key={offset} className={`px-3 py-1.5 rounded-lg text-xs font-mono font-semibold border transition-all ${offset === 0 ? 'bg-foreground text-background border-transparent shadow-xs scale-105' : 'bg-muted/40 text-foreground border-border'}`}>
                          {idSample} {offset === 0 ? '(Next)' : ''}
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    disabled={!isEditMode}
                    onClick={handleApplyAndSave}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-xs font-semibold text-foreground hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <Check className="size-3.5" /> Keep the Changes
                  </button>
                </div>
              </div>

              {/* Associated Lifecycle Workflows & Default Workflow Card */}
              <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
                <div className="border-b border-border pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                      <GitBranch className="size-4 text-primary" /> Associated Lifecycle Workflows &amp; Default Workflow
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      A ticket type can have more than one workflow (e.g. standard fault, fast-track incident, or problem record). Select which workflows apply to {selectedType.name} and pick the default.
                    </p>
                  </div>
                  {selectedType.defaultWorkflow && (
                    <div className="px-2.5 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary text-xs font-semibold shrink-0">
                      Default: {selectedType.defaultWorkflow}
                    </div>
                  )}
                </div>

                {/* Primary Default Workflow Selector */}
                <div className="max-w-md">
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    Primary Default Workflow <span className="text-destructive">*</span>
                  </label>
                  <select
                    value={selectedType.defaultWorkflow || (selectedType.workflows && selectedType.workflows[0]) || ''}
                    disabled={!isEditMode}
                    onChange={(e) => {
                      const defaultWf = e.target.value;
                      const currentWfs = Array.isArray(selectedType.workflows) ? [...selectedType.workflows] : [];
                      if (defaultWf && !currentWfs.includes(defaultWf)) {
                        currentWfs.push(defaultWf);
                      }
                      setTicketTypes(ticketTypes.map((t) => (t.id === selectedType.id ? { ...t, defaultWorkflow: defaultWf, workflows: currentWfs } : t)));
                    }}
                    className="w-full h-8 px-2.5 bg-background border border-input rounded-md text-xs font-semibold text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <option value="">-- Select Default Workflow --</option>
                    {availableWorkflowsList.map((wf) => (
                      <option key={wf.id || wf.name} value={wf.name}>
                        {wf.name} {wf.ticketType ? `(${wf.ticketType})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    New tickets of type "{selectedType.name}" will automatically be assigned this workflow.
                  </p>
                </div>

                {/* Multi-select Workflows for this Ticket Type */}
                <div className="space-y-2 pt-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-semibold text-foreground">
                      Assigned Workflows for {selectedType.name} ({Array.isArray(selectedType.workflows) ? selectedType.workflows.length : 0} assigned)
                    </label>
                    <span className="text-[11px] text-muted-foreground">
                      Click cards below to toggle workflows
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {availableWorkflowsList.map((wf) => {
                      const isAssigned = Array.isArray(selectedType.workflows) && selectedType.workflows.includes(wf.name);
                      const isDefault = selectedType.defaultWorkflow === wf.name;

                      return (
                        <div
                          key={wf.id || wf.name}
                          onClick={() => {
                            if (!isEditMode) return;
                            const current = Array.isArray(selectedType.workflows) ? [...selectedType.workflows] : [];
                            let next: string[];
                            if (current.includes(wf.name)) {
                              next = current.filter((w) => w !== wf.name);
                            } else {
                              next = [...current, wf.name];
                            }
                            let nextDefault = selectedType.defaultWorkflow;
                            if (isDefault && !next.includes(wf.name)) {
                              nextDefault = next[0] || '';
                            } else if (!nextDefault && next.length > 0) {
                              nextDefault = next[0];
                            }
                            setTicketTypes(
                              ticketTypes.map((t) =>
                                t.id === selectedType.id
                                  ? { ...t, workflows: next, defaultWorkflow: nextDefault }
                                  : t
                              )
                            );
                          }}
                          className={cn(
                            "flex items-start gap-2.5 p-3 rounded-lg border transition-all select-none",
                            isEditMode ? "cursor-pointer hover:border-primary/50" : "cursor-default",
                            isAssigned
                              ? "bg-primary/5 border-primary/30 shadow-2xs"
                              : "bg-muted/20 border-border opacity-70"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isAssigned}
                            disabled={!isEditMode}
                            onChange={() => {}}
                            className="mt-0.5 rounded border-border text-primary focus:ring-primary/20 pointer-events-none"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-semibold text-xs text-foreground truncate">
                                {wf.name}
                              </span>
                              {isDefault && (
                                <span className="px-1.5 py-0.2 rounded text-[10px] font-semibold bg-primary text-primary-foreground">
                                  Default
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                              {wf.description || `Lifecycle workflow for ${wf.ticketType || 'tickets'}`}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    disabled={!isEditMode}
                    onClick={handleApplyAndSave}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-xs font-semibold text-foreground hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <Check className="size-3.5" /> Keep the Changes
                  </button>
                </div>
              </div>

              {/* Card & Panel Display Settings (Asset, Vendor, SLA) */}
              <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
                <div className="border-b border-border pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                      <Sliders className="size-4 text-primary" /> Card &amp; Panel Display Settings
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Enable or disable sidebar cards and header badges for tickets of this type. By default, HR ticket types hide Asset and Vendor cards.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Asset Card Toggle */}
                  {(() => {
                    const isHr = ['hr', 'leave', 'onboarding', 'offboarding', 'payroll'].some(k =>
                      String(selectedType.name || '').toLowerCase().includes(k) ||
                      String(selectedType.code || '').toLowerCase().startsWith(k) ||
                      String((selectedType as any).teamName || '').toLowerCase().includes(k)
                    );
                    const isAssetEnabled = selectedType.enableAsset !== undefined ? selectedType.enableAsset : !isHr;
                    const isVendorEnabled = selectedType.enableVendor !== undefined ? selectedType.enableVendor : !isHr;
                    const isSlaEnabled = selectedType.enableSla !== undefined ? selectedType.enableSla : true;

                    return (
                      <>
                        {/* 1. Asset Card */}
                        <div
                          onClick={() => {
                            if (!isEditMode) return;
                            const next = !isAssetEnabled;
                            setTicketTypes(ticketTypes.map((t) => (t.id === selectedType.id ? { ...t, enableAsset: next } : t)));
                          }}
                          className={cn(
                            "flex items-start gap-3 p-3.5 rounded-xl border transition-all select-none",
                            isEditMode ? "cursor-pointer hover:border-primary/50" : "cursor-default",
                            isAssetEnabled ? "bg-primary/5 border-primary/30" : "bg-muted/20 border-border opacity-70"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isAssetEnabled}
                            disabled={!isEditMode}
                            onChange={() => {}}
                            className="mt-1 rounded border-border text-primary focus:ring-primary/20 pointer-events-none"
                          />
                          <div className="space-y-1 flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <Box className="size-3.5 text-primary" />
                              <span className="font-semibold text-xs text-foreground">Asset Card &amp; Badge</span>
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">
                              Display hardware assets, serial tracking, and warranty details.
                            </p>
                            <span className={cn(
                              "inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold mt-1",
                              isAssetEnabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                            )}>
                              {isAssetEnabled ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>

                        {/* 2. Vendor Card */}
                        <div
                          onClick={() => {
                            if (!isEditMode) return;
                            const next = !isVendorEnabled;
                            setTicketTypes(ticketTypes.map((t) => (t.id === selectedType.id ? { ...t, enableVendor: next } : t)));
                          }}
                          className={cn(
                            "flex items-start gap-3 p-3.5 rounded-xl border transition-all select-none",
                            isEditMode ? "cursor-pointer hover:border-primary/50" : "cursor-default",
                            isVendorEnabled ? "bg-primary/5 border-primary/30" : "bg-muted/20 border-border opacity-70"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isVendorEnabled}
                            disabled={!isEditMode}
                            onChange={() => {}}
                            className="mt-1 rounded border-border text-primary focus:ring-primary/20 pointer-events-none"
                          />
                          <div className="space-y-1 flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <Truck className="size-3.5 text-primary" />
                              <span className="font-semibold text-xs text-foreground">Vendor &amp; Supplier</span>
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">
                              Show supplier information, vendor contact details, and warranty links.
                            </p>
                            <span className={cn(
                              "inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold mt-1",
                              isVendorEnabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                            )}>
                              {isVendorEnabled ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>

                        {/* 3. SLA Card */}
                        <div
                          onClick={() => {
                            if (!isEditMode) return;
                            const next = !isSlaEnabled;
                            setTicketTypes(ticketTypes.map((t) => (t.id === selectedType.id ? { ...t, enableSla: next } : t)));
                          }}
                          className={cn(
                            "flex items-start gap-3 p-3.5 rounded-xl border transition-all select-none",
                            isEditMode ? "cursor-pointer hover:border-primary/50" : "cursor-default",
                            isSlaEnabled ? "bg-primary/5 border-primary/30" : "bg-muted/20 border-border opacity-70"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isSlaEnabled}
                            disabled={!isEditMode}
                            onChange={() => {}}
                            className="mt-1 rounded border-border text-primary focus:ring-primary/20 pointer-events-none"
                          />
                          <div className="space-y-1 flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <Clock className="size-3.5 text-primary" />
                              <span className="font-semibold text-xs text-foreground">SLA Card &amp; Timers</span>
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">
                              Display resolution targets, countdown timers, and breach policies.
                            </p>
                            <span className={cn(
                              "inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold mt-1",
                              isSlaEnabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                            )}>
                              {isSlaEnabled ? "Enabled" : "Disabled"}
                            </span>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    disabled={!isEditMode}
                    onClick={handleApplyAndSave}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-4 py-2 text-xs font-semibold text-foreground hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <Check className="size-3.5" /> Keep the Changes
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* TAB 2: FIELD LIST TAB */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {activeTab === 'field_list' && (
            <div className="space-y-4">
              {/* Field List Explanatory Alert */}
              <div className="p-3.5 rounded-xl border border-sky-200 bg-sky-50 dark:bg-sky-950/30 dark:border-sky-800 text-sky-800 dark:text-sky-300 text-xs flex items-start gap-2.5">
                <Info className="size-4 shrink-0 mt-0.5 text-sky-600 dark:text-sky-400" />
                <div className="space-y-0.5">
                  <p className="font-semibold">Field List &amp; Dynamic Form Rules for {selectedType.name}</p>
                  <p className="text-xs leading-relaxed text-sky-700 dark:text-sky-300">
                    To modify field order, adjust visibility, or configure dynamic dependency rules, ensure <strong>Edit Mode</strong> is enabled. Click <strong>Keep the Changes</strong> when done.
                  </p>
                </div>
              </div>

              {/* Field Groups and Items */}
              <div className="space-y-4">
                {fieldGroups.map((groupName) => {
                  const groupFields = selectedType.fields.filter((f) => f.group === groupName);
                  if (groupFields.length === 0) return null;

                  return (
                    <div key={groupName} className="rounded-xl border border-border bg-card overflow-hidden shadow-xs">
                      {/* Group Header */}
                      <div className="bg-muted/40 px-4 py-2.5 border-b border-border flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-xs text-foreground uppercase tracking-wider">
                            {groupName} Field Group
                          </span>
                          <span className="text-xs font-mono px-1.5 py-0.2 rounded bg-background border border-border text-muted-foreground">
                            {groupFields.length} fields
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground italic">
                          Global Group (Overrides active on this ticket type)
                        </span>
                      </div>

                      {/* Fields Table / List */}
                      <div className="divide-y divide-border">
                        {groupFields.map((field) => {
                          const overallIndex = selectedType.fields.findIndex((f) => f.id === field.id);
                          return (
                            <div
                              key={field.id}
                              className={cn(
                                "p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 transition-colors",
                                isEditMode ? "hover:bg-accent/40" : ""
                              )}
                            >
                              {/* Left: Reorder Handles + Label + Badges */}
                              <div className="flex items-center gap-3">
                                {isEditMode && (
                                  <div className="flex flex-col gap-0.5">
                                    <button
                                      type="button"
                                      disabled={overallIndex === 0}
                                      onClick={() => handleMoveField(overallIndex, 'up')}
                                      className="p-1 rounded text-muted-foreground hover:text-foreground disabled:opacity-30 cursor-pointer"
                                      title="Move Up"
                                    >
                                      <ArrowUp className="size-3" />
                                    </button>
                                    <button
                                      type="button"
                                      disabled={overallIndex === selectedType.fields.length - 1}
                                      onClick={() => handleMoveField(overallIndex, 'down')}
                                      className="p-1 rounded text-muted-foreground hover:text-foreground disabled:opacity-30 cursor-pointer"
                                      title="Move Down"
                                    >
                                      <ArrowDown className="size-3" />
                                    </button>
                                  </div>
                                )}

                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-xs text-foreground">{field.label}</span>
                                    <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.2 rounded">
                                      key: {field.key}
                                    </span>
                                    <span className="text-xs font-semibold px-2 py-0.2 rounded-full bg-primary/10 text-primary uppercase">
                                      {field.type}
                                    </span>

                                    {/* Mandatory Badge */}
                                    {field.isMandatory ? (
                                      <span className="text-xs font-semibold px-1.5 py-0.2 rounded bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300">
                                        Mandatory *
                                      </span>
                                    ) : (
                                      <span className="text-xs font-medium px-1.5 py-0.2 rounded bg-muted text-muted-foreground">
                                        Optional
                                      </span>
                                    )}

                                    {/* Portal Visibility Badge */}
                                    {field.isVisiblePortal ? (
                                      <span className="text-xs font-semibold px-1.5 py-0.2 rounded bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 flex items-center gap-1">
                                        <Globe className="size-2.5" /> Portal Visible
                                      </span>
                                    ) : (
                                      <span className="text-xs font-semibold px-1.5 py-0.2 rounded bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 flex items-center gap-1">
                                        <Lock className="size-2.5" /> Agent Only
                                      </span>
                                    )}

                                    {/* Dynamic Visibility Indicator */}
                                    {field.dynamicVisibility && (
                                      <span className="text-xs font-semibold px-1.5 py-0.2 rounded bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300 flex items-center gap-1">
                                        <Sparkles className="size-2.5" /> Dynamic ({field.dynamicVisibility.controllingFieldName} = {field.dynamicVisibility.triggerValues.join(', ')})
                                      </span>
                                    )}
                                  </div>

                                  {field.helpText && (
                                    <p className="text-xs text-muted-foreground">{field.helpText}</p>
                                  )}
                                </div>
                              </div>

                              {/* Right: Actions (Mandatory Toggle, Pencil Edit, Delete) */}
                              <div className="flex items-center gap-2 self-end sm:self-center">
                                {isEditMode ? (
                                  <>
                                    {/* Mandatory Quick Toggle */}
                                    <button
                                      type="button"
                                      onClick={() => handleToggleMandatory(field.id)}
                                      className={cn(
                                        "px-2.5 py-1 text-xs font-semibold rounded border transition-colors cursor-pointer",
                                        field.isMandatory
                                          ? "bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-950/40 dark:border-rose-800"
                                          : "bg-background border-border text-muted-foreground hover:text-foreground"
                                      )}
                                      title="Toggle Mandatory Requirement"
                                    >
                                      {field.isMandatory ? 'Required' : 'Make Required'}
                                    </button>

                                    {/* Pencil Icon (Edit Field Details & Dynamic Rules) */}
                                    <button
                                      type="button"
                                      onClick={() => setEditingField({ ...field })}
                                      className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors cursor-pointer"
                                      title="Pencil Edit Field & Dynamic Visibility Rules"
                                    >
                                      <Edit className="size-4 text-primary" />
                                    </button>

                                    {/* Delete Button (if custom field) */}
                                    {!field.isSystem && (
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteField(field.id, field.label)}
                                        className="p-1.5 text-muted-foreground hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg transition-colors cursor-pointer"
                                        title="Remove Field"
                                      >
                                        <Trash2 className="size-4" />
                                      </button>
                                    )}
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingField({ ...field });
                                    }}
                                    className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg transition-colors cursor-pointer"
                                    title="View & Edit Field Properties"
                                  >
                                    <Edit className="size-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-end pt-3">
                <button type="button" onClick={() => setShowAddFieldModal(true)} className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm font-medium hover:bg-secondary">
                  <Plus className="size-4" /> Add field
                </button>
              </div>
            </div>
          )}
          <div className="sticky -bottom-5 -mx-5 mt-auto flex justify-end gap-2 border-t border-border bg-card px-5 py-4">
            <button type="button" onClick={handleBackToList} className="h-9 rounded-lg border border-border bg-card px-4 text-sm font-medium hover:bg-secondary">Cancel</button>
            <button type="button" onClick={handleApplyAndSave} className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"><Save className="size-4" /> Save changes</button>
          </div>
        </DialogContent>
        </Dialog>
      )}

      {/* 3. PENCIL EDIT FIELD MODAL (DYNAMIC VISIBILITY & MANDATORY SETTINGS) */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {editingField && (
        <Dialog open onOpenChange={(open) => { if (!open) setEditingField(null); }}>
          <DialogContent side="right" hideClose className="gap-0 overflow-hidden p-0" aria-describedby={undefined}>
            <DialogTitle className="sr-only">Edit field</DialogTitle>
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20">
              <div>
                <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                  <Edit className="size-4 text-primary" /> Edit Field: {editingField.label}
                </h3>
                <p className="text-xs text-muted-foreground">Adjust visibility, mandatory requirement, and dynamic conditional rules.</p>
              </div>
              <button onClick={() => setEditingField(null)} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent">
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSaveFieldProperties} className="flex-1 flex flex-col overflow-hidden text-xs">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-foreground block mb-1">Field Label *</label>
                  <input
                    type="text"
                    required
                    value={editingField.label}
                    onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                    className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none"
                  />
                </div>
                <div>
                  <label className="font-semibold text-foreground block mb-1">Field Group</label>
                  <select
                    value={editingField.group}
                    onChange={(e) => setEditingField({ ...editingField, group: e.target.value as any })}
                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none"
                  >
                    <option value="Details">Details</option>
                    <option value="Asset &amp; Configuration">Asset &amp; Configuration</option>
                    <option value="Requester Info">Requester Info</option>
                    <option value="Resolution &amp; Closure">Resolution &amp; Closure</option>
                    <option value="Custom">Custom Fields</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-semibold text-foreground block mb-1">Placeholder Text</label>
                <input
                  type="text"
                  value={editingField.placeholder || ''}
                  onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
                  placeholder="e.g. Enter detailed symptoms..."
                  className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none"
                />
              </div>

              <div>
                <label className="font-semibold text-foreground block mb-1">Help / Tooltip Guidance</label>
                <input
                  type="text"
                  value={editingField.helpText || ''}
                  onChange={(e) => setEditingField({ ...editingField, helpText: e.target.value })}
                  placeholder="Explains what end-users or agents should enter"
                  className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none"
                />
              </div>

              {/* Mandatory & Visibility Settings */}
              <div className="p-3.5 rounded-xl border border-border bg-muted/20 space-y-2.5">
                <h4 className="font-semibold text-xs text-foreground uppercase tracking-wider">
                  Mandatory &amp; Portal Visibility
                </h4>

                <label className="flex items-center gap-2 text-xs font-semibold text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingField.isMandatory}
                    onChange={(e) => setEditingField({ ...editingField, isMandatory: e.target.checked })}
                    className="size-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span>Mandatory Field (Ensures value is populated prior to ticket submission)</span>
                </label>

                <label className="flex items-center gap-2 text-xs font-semibold text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingField.isVisiblePortal}
                    onChange={(e) => setEditingField({ ...editingField, isVisiblePortal: e.target.checked })}
                    className="size-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span>Visible on Self-Service Portal (End-user submission)</span>
                </label>

                <label className="flex items-center gap-2 text-xs font-semibold text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingField.isVisibleAgent}
                    onChange={(e) => setEditingField({ ...editingField, isVisibleAgent: e.target.checked })}
                    className="size-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span>Visible in Agent Application Interface</span>
                </label>
              </div>

              {/* ═══════════════════════════════════════════════════════════ */}
              {/* DYNAMIC FIELD VISIBILITY BUILDER */}
              {/* ═══════════════════════════════════════════════════════════ */}
              <div className="p-3.5 rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50/40 dark:bg-purple-950/20 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="size-4 text-purple-600 dark:text-purple-400" />
                    <span className="font-semibold text-xs text-foreground">Dynamic Field Visibility</span>
                  </div>

                  <label className="flex items-center gap-1.5 text-xs font-semibold text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editingField.dynamicVisibility}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const otherField = selectedType?.fields.find((f) => f.id !== editingField.id && f.type === 'select');
                          setEditingField({
                            ...editingField,
                            dynamicVisibility: {
                              controllingFieldId: otherField?.id || 'f-3',
                              controllingFieldName: otherField?.label || 'Category',
                              triggerValues: ['Hardware'],
                            },
                          });
                        } else {
                          setEditingField({
                            ...editingField,
                            dynamicVisibility: undefined,
                          });
                        }
                      }}
                      className="size-4 rounded border-border text-purple-600 focus:ring-purple-600"
                    />
                    Enable Dynamic Dependency
                  </label>
                </div>

                <p className="text-xs text-muted-foreground">
                  Set this field to be dynamically visible based on the value of another field (e.g. show <em>Affected Asset</em> only when <em>Category</em> = <em>Hardware</em>).
                </p>

                {editingField.dynamicVisibility && (
                  <div className="space-y-2.5 pt-2 border-t border-purple-200 dark:border-purple-800 animate-in fade-in duration-150">
                    <div className="grid grid-cols-2 gap-2.5">
                      <div>
                        <label className="font-semibold text-foreground block mb-1">Controlling Field *</label>
                        <select
                          value={editingField.dynamicVisibility.controllingFieldId}
                          onChange={(e) => {
                            const targetField = selectedType?.fields.find((f) => f.id === e.target.value);
                            setEditingField({
                              ...editingField,
                              dynamicVisibility: {
                                ...editingField.dynamicVisibility!,
                                controllingFieldId: e.target.value,
                                controllingFieldName: targetField?.label || 'Category',
                                triggerValues: targetField?.options ? [targetField.options[0]] : ['Hardware'],
                              },
                            });
                          }}
                          className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none"
                        >
                          {selectedType?.fields
                            .filter((f) => f.id !== editingField.id)
                            .map((f) => (
                              <option key={f.id} value={f.id}>{f.label} ({f.group})</option>
                            ))}
                        </select>
                      </div>

                      <div>
                        <label className="font-semibold text-foreground block mb-1">Condition Trigger</label>
                        <span className="h-8 w-full flex items-center px-2.5 rounded-md border border-input bg-muted/40 font-mono text-xs text-muted-foreground">
                          EQUALS (Match any value)
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="font-semibold text-foreground block mb-1">
                        Select Trigger Values (Comma-separated or list) *
                      </label>
                      <input
                        type="text"
                        value={editingField.dynamicVisibility.triggerValues.join(', ')}
                        onChange={(e) => {
                          const values = e.target.value.split(',').map((v) => v.trim()).filter(Boolean);
                          setEditingField({
                            ...editingField,
                            dynamicVisibility: {
                              ...editingField.dynamicVisibility!,
                              triggerValues: values,
                            },
                          });
                        }}
                        placeholder="e.g. Hardware, IT Equipment"
                        className="h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none"
                      />
                      <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                        This field will ONLY populate on the form when <strong>{editingField.dynamicVisibility.controllingFieldName}</strong> contains one of the above values.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              </div>

              <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingField(null)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 cursor-pointer shadow-xs"
                >
                  Apply Field Settings
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* 4. 'ADD +' FIELD MODAL (EXISTING SYSTEM FIELDS vs CUSTOM FIELD) */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {showAddFieldModal && (
        <Dialog open onOpenChange={(open) => { if (!open) setShowAddFieldModal(false); }}>
          <DialogContent side="right" hideClose className="gap-0 overflow-hidden p-0" aria-describedby={undefined}>
            <DialogTitle className="sr-only">Add field</DialogTitle>
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20">
              <div>
                <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                  <Plus className="size-4 text-primary" /> Add Field to {selectedType?.name}
                </h3>
                <p className="text-xs text-muted-foreground">Select from existing system fields or define a new custom field.</p>
              </div>
              <button onClick={() => setShowAddFieldModal(false)} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent">
                <X className="size-4" />
              </button>
            </div>

            {/* Modal Tabs: Underline Style */}
            <div className="flex border-b border-border px-4 bg-card">
              <button
                type="button"
                onClick={() => setAddFieldTab('existing')}
                className={cn(
                  "py-2.5 px-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer -mb-px flex items-center gap-1.5",
                  addFieldTab === 'existing'
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                Existing System Fields
              </button>
              <button
                type="button"
                onClick={() => setAddFieldTab('custom')}
                className={cn(
                  "py-2.5 px-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer -mb-px flex items-center gap-1.5",
                  addFieldTab === 'custom'
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                Create Custom Field
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">

            {/* TAB A: EXISTING SYSTEM FIELDS */}
            {addFieldTab === 'existing' && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">Choose from reusable system fields across your ITSM workspace:</p>
                <div className="divide-y divide-border border border-border rounded-xl overflow-hidden bg-background max-h-72 overflow-y-auto">
                  {SYSTEM_FIELDS_BANK.map((bankField) => {
                    const alreadyAdded = selectedType?.fields.some((f) => f.key === bankField.key);
                    return (
                      <div key={bankField.id} className="p-3 flex items-center justify-between hover:bg-muted/30 transition-colors">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-xs text-foreground">{bankField.label}</span>
                            <span className="text-xs font-semibold px-1.5 py-0.2 rounded bg-primary/10 text-primary uppercase">
                              {bankField.type}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Group: <strong className="text-foreground">{bankField.group}</strong> • key: <code className="font-mono">{bankField.key}</code>
                          </p>
                        </div>

                        <button
                          type="button"
                          disabled={alreadyAdded}
                          onClick={() => handleAddExistingField(bankField)}
                          className={cn(
                            "px-3 py-1 text-xs font-semibold rounded-md transition-colors cursor-pointer",
                            alreadyAdded
                              ? "bg-muted text-muted-foreground opacity-50 cursor-not-allowed"
                              : "bg-primary text-primary-foreground hover:bg-primary/90"
                          )}
                        >
                          {alreadyAdded ? 'Added' : 'Add to Form'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* TAB B: CREATE NEW CUSTOM FIELD */}
            {addFieldTab === 'custom' && (
              <form onSubmit={handleCreateCustomField} className="space-y-3 text-xs">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Field Label *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Asset Warranty Date"
                      value={customFieldForm.label}
                      onChange={(e) => setCustomFieldForm({ ...customFieldForm, label: e.target.value })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Field Group</label>
                    <select
                      value={customFieldForm.group}
                      onChange={(e) => setCustomFieldForm({ ...customFieldForm, group: e.target.value as any })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none"
                    >
                      <option value="Details">Details</option>
                      <option value="Asset &amp; Configuration">Asset &amp; Configuration</option>
                      <option value="Requester Info">Requester Info</option>
                      <option value="Resolution &amp; Closure">Resolution &amp; Closure</option>
                      <option value="Custom">Custom Fields</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Field Type *</label>
                    <select
                      value={customFieldForm.type}
                      onChange={(e) => setCustomFieldForm({ ...customFieldForm, type: e.target.value as any })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none"
                    >
                      <option value="text">Single-Line Text</option>
                      <option value="textarea">Multi-Line Text Area</option>
                      <option value="select">Dropdown / Select</option>
                      <option value="number">Numeric Input</option>
                      <option value="date">Date Picker</option>
                      <option value="user">User / Employee Picker</option>
                      <option value="asset">Hardware Asset Picker</option>
                      <option value="checkbox">Checkbox Boolean</option>
                    </select>
                  </div>

                  <div>
                    <label className="font-semibold text-foreground block mb-1">Placeholder</label>
                    <input
                      type="text"
                      placeholder="e.g. Enter serial number"
                      value={customFieldForm.placeholder}
                      onChange={(e) => setCustomFieldForm({ ...customFieldForm, placeholder: e.target.value })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none"
                    />
                  </div>
                </div>

                {customFieldForm.type === 'select' && (
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Dropdown Options (Comma-separated) *</label>
                    <input
                      type="text"
                      value={customFieldForm.options?.join(', ')}
                      onChange={(e) => setCustomFieldForm({ ...customFieldForm, options: e.target.value.split(',').map((v) => v.trim()) })}
                      placeholder="Option 1, Option 2, Option 3"
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none font-mono"
                    />
                  </div>
                )}

                <div className="pt-2 flex items-center gap-4">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customFieldForm.isMandatory}
                      onChange={(e) => setCustomFieldForm({ ...customFieldForm, isMandatory: e.target.checked })}
                      className="size-4 rounded border-border text-primary focus:ring-primary"
                    />
                    Mandatory
                  </label>

                  <label className="flex items-center gap-1.5 text-xs font-semibold text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customFieldForm.isVisiblePortal}
                      onChange={(e) => setCustomFieldForm({ ...customFieldForm, isVisiblePortal: e.target.checked })}
                      className="size-4 rounded border-border text-primary focus:ring-primary"
                    />
                    Portal Visible
                  </label>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-border mt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddFieldModal(false)}
                    className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 cursor-pointer shadow-xs"
                  >
                    Create &amp; Add Field
                  </button>
                </div>
              </form>
            )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
